import { Box, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FIELD_LABEL_SX, FilterPopoverShell } from './FilterPopoverShell';

export interface PbsEmployeeIdFilterPopoverProps {
  /** Applied substring filter (case-insensitive). Empty string = inactive. */
  value: string;
  onChange: (next: string) => void;
}

/**
 * Filter control for the Employee ID column header: icon opens a popover with a
 * substring match, mirroring the Value column's filter (draft state + Clear/Apply)
 * so both column filters look and behave the same way.
 */
export const PbsEmployeeIdFilterPopover: React.FC<
  PbsEmployeeIdFilterPopoverProps
> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);

  const active = value.trim().length > 0;
  const draftActive = draft.trim().length > 0;

  const handleApply = () => onChange(draft.trim());
  const handleClear = () => {
    onChange('');
    setDraft('');
  };

  return (
    <FilterPopoverShell
      active={active}
      triggerAriaLabel={t(
        'pages:dashboards.peopleBalanceSheet.cellDrillDown.employeeIdFilterOpen',
        { defaultValue: 'Filter by Employee ID' }
      )}
      title={t(
        'pages:dashboards.peopleBalanceSheet.cellDrillDown.employeeIdFilterTitle',
        { defaultValue: 'Filter Employee ID' }
      )}
      onOpen={() => setDraft(value)}
      onApply={handleApply}
      onClear={handleClear}
      applyDisabled={!draftActive}
      clearDisabled={!active && !draftActive}
      applyLabel={t(
        'pages:dashboards.peopleBalanceSheet.cellDrillDown.filterApply',
        { defaultValue: 'Apply' }
      )}
      clearLabel={t(
        'pages:dashboards.peopleBalanceSheet.cellDrillDown.filterClear',
        { defaultValue: 'Clear' }
      )}
    >
      <Box>
        <Typography component="label" sx={FIELD_LABEL_SX}>
          {t(
            'pages:dashboards.peopleBalanceSheet.cellDrillDown.employeeIdFilterLabel',
            { defaultValue: 'Employee ID contains' }
          )}
        </Typography>
        <TextField
          size="small"
          fullWidth
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t(
            'pages:dashboards.peopleBalanceSheet.cellDrillDown.employeeIdFilterPlaceholder',
            { defaultValue: 'e.g. 12345' }
          )}
        />
      </Box>
    </FilterPopoverShell>
  );
};
